import {FormInstance, Rule} from 'antd/es/form';
import {isNil} from 'lodash-es';
import {Dispatch, SetStateAction} from 'react';

import {getRegExpPassword} from 'components/form/utils/form.utils';
import {EAdministrationOpenTextFieldsService} from 'components/table/action/open-launch-program-modal/open-launch-program-modal.constants';
import {IFieldMeta, EFieldType, IValidationRules} from 'entities/metadata';
import {IFormEntityData} from 'modules/data/data-types';

import {EFormMode} from '../form-types';

export enum EMaskValidation {
    dynamic = 'dynamic',
    checkValueLength = 'checkValueLength',
    checkNumericalValue = 'checkNumericalValue',
    checkIntegerValue = 'checkIntegerValue',
    checkSpecialSymbols = 'checkSpecialSymbols',
    email = 'email',
    fromProperty = 'fromProperty',
    addictiveness = 'addictiveness',
    requiredMatchPasswordField = 'requiredMatchPasswordField',
    onlyWords = 'onlyWords',
    wordsWithDots = 'wordsWithDots',
    shortUrl = 'shortUrl',
    checkByDateLater = 'checkByDateLater',
    errorWatcher = 'errorWatcher',
    checkFieldUniqueness = 'checkFieldUniqueness',
    fromPropertyListPassword = 'fromPropertyListPassword',
    fromPropertyListPasswordLength = 'fromPropertyListPasswordLength',
    checkDateAfterNow = 'checkDateAfterNow',
    anotherFieldSimilarity = 'anotherFieldSimilarity',
    passwordProhibitedCombinations = 'passwordProhibitedCombinations',
    documentIdentifiers = 'documentIdentifiers',
    monitorServiceOpenTextDateFrom = 'monitorServiceOpenTextDateFrom',
    monitorServiceOpenTextDateTo = 'monitorServiceOpenTextDateTo',
    monitorServiceOpenTextDocIds = 'monitorServiceOpenTextDocIds',
    cronExpression = 'cronExpression',
}

const defaultErrorMessage = 'Введите корректное значение';

export interface IFieldValidationResolverProps {
    validationRules?: IValidationRules[];
    fieldMeta: Pick<IFieldMeta, 'type' | 'label' | 'isDisabled' | 'isRequired' | 'dependentsFieldKey' | 'dateFormat'>;
    validationRulesByWatcher?: IValidationRules[];
    formData?: IFormEntityData | undefined;
    initialValue?: any;
    form?: FormInstance | undefined;
    requiredMatchPasswordField?: string | undefined;
    propertyCode?: string | undefined;
    regularFromProperty?: string;
    setRegularFromProperty?: Dispatch<SetStateAction<string>>;
    isRequiredByWatcher?: boolean;
    isAllowedEmptyByWatcher?: boolean;
    settings?: {
        checkFieldUniquenessArrayPath?: string;
        checkFieldUniquenessFieldKey?: string;
    };
    dispatch?: Dispatch<unknown>;
    propertySettings?: Record<string, any>;
    formMode?: EFormMode;
}

export const fieldValidationResolver = (
    {
        form,
        fieldMeta,
        validationRules = [],
        validationRulesByWatcher = [],
        isAllowedEmptyByWatcher,
        isRequiredByWatcher,
        requiredMatchPasswordField,
        propertyCode,
        regularFromProperty,
        formData,
        settings = {},
        propertySettings,
        formMode,
    }: IFieldValidationResolverProps,
): Rule[] => {
    const {
        isDisabled, isRequired: isRequiredMeta, type, label,
    } = fieldMeta;
    const {checkFieldUniquenessArrayPath, checkFieldUniquenessFieldKey} = settings;

    const isRequired = !isAllowedEmptyByWatcher && (isRequiredMeta || isRequiredByWatcher);

    const rules: Rule[] = [{
        required: isRequired,
        message: isRequired
            ? `Поле '${fieldMeta.label}' должно быть заполнено` : '',
    }];

    if (type !== EFieldType.FILE && type !== EFieldType.MULTI_VALUE_SET) {
        rules.push({
            pattern: isRequired ? /^(?!\s*$).+/ : /^/,
            message: isRequired
                ? `Поле '${fieldMeta.label}' не должно быть пустым` : '',
        });
    }

    const newRule: Rule[] = [...validationRules, ...validationRulesByWatcher].map(item => {
        const {
            mask,
            regExpKey,
            minValueLength,
            maxValueLength,
            minNumericalValue,
            maxNumericalValue,
            errorMessage,
            regExp,
        } = item;
        if (mask === EMaskValidation.dynamic) {
            const regExpValue = regExpKey && formData?.data[regExpKey]?.toString();
            return (() => ({
                validator(_: any, value: string) {
                    if (value && value.match(String(regExpValue))) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(
                            (errorMessage && formData?.data[errorMessage]?.toString())
                            || defaultErrorMessage,
                        ),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.documentIdentifiers) {
            return (() => ({
                validator(_: any, value: string) {
                    if (!value && !isRequired) {
                        return Promise.resolve();
                    }

                    if (!value) {
                        return Promise.reject(new Error(errorMessage ?? 'Значения должны быть разделены символом ","'));
                    }

                    // Быстрая проверка на наличие хотя бы одного значения после последней запятой
                    if (value[value.length - 1] === ',') {
                        return Promise.reject(new Error(errorMessage ?? 'Значения должны быть разделены символом ","'));
                    }

                    // Проверка количества элементов
                    const commaCount = value.match(/,/g)?.length || 0;
                    if (commaCount >= 800) {
                        return Promise.reject(
                            new Error(errorMessage ?? 'Превышено максимальное количество значений'),
                        );
                    }

                    return Promise.resolve();
                },
            }));
        }
        if (mask === EMaskValidation.shortUrl) {
            return {
                validator(_: any, value: string) {
                    if (!value && !isRequired) return Promise.resolve();

                    const PATH_SEGMENT = '[\\w\\-._~!$\'()*+,;=:@%]+';
                    const PATH = `(?:${PATH_SEGMENT}(?:/${PATH_SEGMENT})*)?`;
                    const QUERY = '(?:\\?[\\w\\-._~!$\'()*+,;=:@%/?]*)?';

                    const safeShortUrlRegex = new RegExp(`^/${PATH}${QUERY}$`);

                    if (!safeShortUrlRegex.test(value)) {
                        return Promise.reject(new Error(errorMessage ?? 'Недопустимый формат URL'));
                    }
                    return Promise.resolve();
                },
            };
        }
        if (mask === EMaskValidation.onlyWords) {
            return (() => ({
                validator(_: any, value: string) {
                    const regex = /^[\w]*$/;
                    if ((!value && !isRequired) || (value && value.match(regex))) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error(
                        errorMessage ?? 'Допустимы только символы "a-z", "A-Z", "0-9" и "_"',
                    ));
                },
            }));
        }
        if (mask === EMaskValidation.wordsWithDots) {
            return (() => ({
                validator(_: any, value: string) {
                    const regex = /^[\w.]*$/;
                    if ((!value && !isRequired) || (value && value.match(regex))) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error(
                        errorMessage ?? 'Допустимы только символы "a-z", "A-Z", "0-9", "_" и "."',
                    ));
                },
            }));
        }
        if (mask === EMaskValidation.checkValueLength) {
            return (() => ({
                validator(_: any, value: string) {
                    if (!isNil(maxValueLength) && !isNil(minValueLength)) {
                        if ((!value && !isRequired)
                            || (value && value.toString().length <= maxValueLength
                                && value.toString().length >= minValueLength)) {
                            return Promise.resolve();
                        }
                    }
                    return Promise.reject(new Error(errorMessage ?? defaultErrorMessage));
                },
            }));
        }
        if (mask === EMaskValidation.checkSpecialSymbols) {
            return (() => ({
                validator(_: any, value: string) {
                    if ((!value && !isRequired)
                        || (value && value.toString().match(regExp || '') && !isDisabled)) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error(errorMessage ?? defaultErrorMessage));
                },
            }));
        }
        if (mask === EMaskValidation.checkNumericalValue) {
            return (() => ({
                validator(_: any, value: string) {
                    const leadingZerosRegex = /^0[0-9].*$/;
                    if (leadingZerosRegex.test(value)) {
                        return Promise.reject(
                            new Error(errorMessage ?? defaultErrorMessage),
                        );
                    }
                    if (!isNil(maxNumericalValue) && !isNil(minNumericalValue)) {
                        if (Number(value) <= maxNumericalValue && Number(value) >= minNumericalValue) {
                            return Promise.resolve();
                        }
                        return Promise.reject(
                            new Error(errorMessage ?? defaultErrorMessage),
                        );
                    }
                    if (!isNil(minNumericalValue) && Number(value) >= minNumericalValue) {
                        return Promise.resolve();
                    }
                    if (!isNil(maxNumericalValue) && Number(value) <= maxNumericalValue) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(errorMessage ?? defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.checkIntegerValue) {
            return (() => ({
                validator(_: any, value: string) {
                    if (/^-?\d+$/.test(value)) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(errorMessage ?? defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.email) {
            return (
                {
                    type: 'email',
                    message: errorMessage,
                }
            );
        }
        if (mask === EMaskValidation.addictiveness) {
            return (({getFieldValue}) => ({
                validator() {
                    if (getFieldValue(fieldMeta?.dependentsFieldKey || '') === undefined) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error(errorMessage || 'Error'));
                },
            }));
        }
        if (mask === EMaskValidation.fromPropertyListPassword) {
            return (() => ({
                validator(_: any, value: string) {
                    // Символы полученные из "Настройки приложения"
                    const specialSymbols = propertySettings?.ALLOWED_CHARACTERS_IN_PASSWORD;
                    const regExpPassword = getRegExpPassword(specialSymbols);
                    if ((!value && !isRequired)
                        || (value && value.toString().match(regExpPassword || '') && !isDisabled)
                        || (formMode === EFormMode.EDIT && value.length === 1)) {
                        return Promise.resolve();
                    }
                    const passwordErrorMessage = errorMessage && `${errorMessage} ${specialSymbols}`;
                    return Promise.reject(new Error(passwordErrorMessage ?? defaultErrorMessage));
                },
            }));
        }
        if (mask === EMaskValidation.passwordProhibitedCombinations) {
            return (() => ({
                validator(_: any, value: string) {
                    if (!value) return Promise.resolve();

                    const prohibitedCombinations = propertySettings?.NOT_ALLOWED_COMBINATIONS;

                    if (
                        !value.includes(prohibitedCombinations)
                      || prohibitedCombinations === ''
                      || prohibitedCombinations === undefined
                    ) return Promise.resolve();

                    const errMessage = errorMessage
                        ? `${errorMessage}: ${prohibitedCombinations}`
                        : defaultErrorMessage;
                    return Promise.reject(new Error(errMessage));
                },
            }));
        }
        if (mask === EMaskValidation.fromPropertyListPasswordLength) {
            return (() => ({
                validator(_: any, value: string) {
                    // Длина пароля, полученная из "Настройки приложения"
                    const passwordLengthMax = propertySettings?.PASSWORD_LENGTH_MAX;
                    const passwordLengthMin = propertySettings?.PASSWORD_LENGTH_MIN;
                    if (formMode === EFormMode.EDIT && !value && !isRequired) {
                        return Promise.resolve();
                    }
                    if (passwordLengthMin && value && value.toString().length >= Number(passwordLengthMin)
                        && passwordLengthMax && value && value.toString().length <= Number(passwordLengthMax)) {
                        return Promise.resolve();
                    }
                    const passwordErrorMessage = errorMessage?.replace(
                        'passwordLengthMax', passwordLengthMax,
                    )?.replace('passwordLengthMin', passwordLengthMin);
                    return Promise.reject(
                        new Error(passwordErrorMessage ?? defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.requiredMatchPasswordField) {
            if (requiredMatchPasswordField) {
                return (() => ({
                    validator(_: any, value: string) {
                        const otherPasswordFieldValue = form?.getFieldValue(requiredMatchPasswordField);
                        if (otherPasswordFieldValue === value || (!otherPasswordFieldValue && !value)) {
                            return Promise.resolve();
                        }
                        return Promise.reject(new Error(errorMessage));
                    },
                }));
            }
        }
        if (mask === EMaskValidation.anotherFieldSimilarity) {
            return (({getFieldValue}) => ({
                validator(_: any, value: string) {
                    const dependentFieldValue = getFieldValue(fieldMeta?.dependentsFieldKey || '');
                    if (dependentFieldValue !== value) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(errorMessage || defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.fromProperty) {
            if (propertyCode) {
                const regValue = new RegExp(String(regularFromProperty));
                return (() => ({
                    validator(_: any, value: string) {
                        if (value && value.match(regValue)) {
                            return Promise.resolve();
                        }
                        return Promise.reject(
                            new Error(errorMessage || defaultErrorMessage),
                        );
                    },
                }));
            }
        }
        if (mask === EMaskValidation.checkByDateLater) {
            return (({getFieldValue}) => ({
                validator(_: any, value: string) {
                    const endDate = getFieldValue(fieldMeta?.dependentsFieldKey || '');
                    if (endDate <= value) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(errorMessage || defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.errorWatcher) {
            return (() => ({
                validator() {
                    return Promise.reject(
                        new Error(errorMessage || defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.checkFieldUniqueness) {
            return ({getFieldValue}) => ({
                validator: (_, value) => {
                    const values = getFieldValue(checkFieldUniquenessArrayPath || '');
                    const valuesConverted = Object
                        .entries(values ?? [])
                        .map(([key, val]) => ({
                            keyName: key,
                            ...(val as Object),
                        }));
                    const sameKeys = valuesConverted
                        ?.filter((entry: any) => !!value
                            && entry?.[checkFieldUniquenessFieldKey || ''] === value);
                    if (sameKeys?.length > 1) {
                        return Promise.reject(new Error(errorMessage));
                    }
                    return Promise.resolve();
                },
            });
        }
        if (mask === EMaskValidation.checkDateAfterNow) {
            return (() => ({
                validator(_: any, value: string) {
                    if (new Date(value) > new Date()) {
                        return Promise.resolve();
                    }
                    return Promise.reject(
                        new Error(errorMessage ?? defaultErrorMessage),
                    );
                },
            }));
        }
        if (mask === EMaskValidation.monitorServiceOpenTextDocIds) {
            return (({getFieldValue}) => ({
                validator(_, value) {
                    if (getFieldValue(EAdministrationOpenTextFieldsService.P_DATE_FROM) !== undefined
                    || getFieldValue(EAdministrationOpenTextFieldsService.P_DATE_TO) !== undefined) {
                        if (value) {
                            return Promise.reject(new Error(errorMessage || 'Некорректный набор параметров'));
                        }
                    } else if (getFieldValue(EAdministrationOpenTextFieldsService.P_DATE_FROM) === undefined
                    && getFieldValue(EAdministrationOpenTextFieldsService.P_DATE_TO) === undefined) {
                        if (!value) {
                            return Promise.reject(new Error(errorMessage || `Поле '${label}' должно быть заполнено`));
                        }
                    }
                    return Promise.resolve();
                },
            }));
        }
        if (mask === EMaskValidation.monitorServiceOpenTextDateFrom) {
            return (({getFieldValue}) => ({
                validator(_, value) {
                    if (getFieldValue(EAdministrationOpenTextFieldsService.P_DATE_TO) !== undefined
                    && !getFieldValue(EAdministrationOpenTextFieldsService.P_DOC_IDS)) {
                        if (!value) {
                            return Promise.reject(new Error(errorMessage || `Поле '${label}' должно быть заполнено`));
                        }
                    }
                    return Promise.resolve();
                },
            }));
        }
        if (mask === EMaskValidation.monitorServiceOpenTextDateTo) {
            return (({getFieldValue}) => ({
                validator(_, value) {
                    if (getFieldValue(EAdministrationOpenTextFieldsService.P_DATE_FROM) !== undefined
                    && !getFieldValue(EAdministrationOpenTextFieldsService.P_DOC_IDS)) {
                        if (!value) {
                            return Promise.reject(new Error(errorMessage || `Поле '${label}' должно быть заполнено`));
                        }
                    }
                    return Promise.resolve();
                },
            }));
        }
        if (mask === EMaskValidation.cronExpression) {
            return (() => ({
                validator(_, value) {
                    if (value && value.trim() !== '') {
                        const segments = value.trim().split(/\s+/);
                        if (segments.length !== 6) {
                            return Promise.reject(new Error('Cron выражение должно содержать 6 сегментов'));
                        }
                        let segmentError = '';
                        const errors: string[] = [];

                        // секунда или минута (0-59)
                        const secondOrMinuteRegex = new RegExp([
                            '^(?:',
                            '[1-5]?[0-9]|',
                            '\\*|',
                            '[0-5]?[0-9](?:,[0-5]?[0-9])*(?:/[1-5]?[0-9])?|',
                            '\\?|',
                            '\\*/[1-5]?[0-9]|',
                            '/[1-5]?[0-9]',
                            ')$'
                        ].join(''));

                        // час (0 - 23)
                        const hourRegex = new RegExp([
                            '^(?:',
                            '[0-1]?[0-9]|',
                            '2[0-3]|',
                            '\\*|',
                            '[0-1]?[0-9](?:,[0-1]?[0-9])*(?:/[1-2]?[0-9])?|',
                            '\\?|',
                            '\\*/[0-1]?[0-9]|',
                            '/[0-1]?[0-9]',
                            ')$'
                        ].join(''));

                        // день месяца (1 - 31)
                        const dayOfMonthRegex = new RegExp([
                            '^(?:',
                            '[1-9]|',
                            '[12][0-9]|',
                            '3[01]|',
                            '\\*|',
                            '[1-9](?:,[1-9]|[12][0-9]|3[01])*(?:/[1-9]|[12][0-9]|3[01])?|',
                            '\\?|',
                            '\\*/[1-9]|[12][0-9]|3[01]|',
                            '/[1-9]|[12][0-9]|3[01]',
                            ')$'
                        ].join(''));

                        // месяц (1 - 12) (или JAN-DEC)
                        const monthRegex = new RegExp([
                            '^(?:',
                            '[1-9]|',
                            '1[0-2]|',
                            'JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|',
                            '\\*|',
                            '(?:[1-9]|1[0-2])(?:,[1-9]|1[0-2])*(?:/[1-9]|[1-2])?|',
                            '\\?|',
                            '\\*/(?:[1-9]|1[0-2])|',
                            '/(?:[1-9]|1[0-2])',
                            ')$'
                        ].join(''));

                        // день недели (0 - 7)
                        const dayOfWeekRegex = new RegExp([
                            '^(?:',
                            '[0-7]|',
                            '\\*|',
                            '[0-7](?:,[0-7])*(?:/[1-7])?|',
                            '\\?|',
                            '/[0-7]',
                            ')$'
                        ].join(''));

                        const validators = [
                            {regex: secondOrMinuteRegex, field: 'секунды', range: '0-59'},
                            {regex: secondOrMinuteRegex, field: 'минуты', range: '0-59'},
                            {regex: hourRegex, field: 'часы', range: '0-23'},
                            {regex: dayOfMonthRegex, field: 'дни', range: '1-31'},
                            {regex: monthRegex, field: 'месяц', range: '1-12 или JAN-DEC'},
                            {regex: dayOfWeekRegex, field: 'день недели', range: '0-7'},
                        ];

                        segments.forEach((segment: string, index: number) => {
                            const validator = validators[index];

                            if (!validator.regex.test(segment)) {
                                errors.push(`${validator.field} - диапазон ${validator.range}`);
                            }
                        });

                        if (errors?.length) {
                            segmentError = `Неверное значение для: ${errors.join(', ')}`;
                            return Promise.reject(new Error(errorMessage || segmentError));
                        }
                    }
                    return Promise.resolve();
                },
            }));
        }
        return {
            validator(_: any, value: any) {
                let valueStr;

                if (fieldMeta.type === EFieldType.RICH_TEXT) {
                    if (value != null) {
                        // Безопасный regex для удаления тегов
                        valueStr = String(value)
                            .replace(
                                /<\/?[a-z][a-z0-9]*(?:\s+[^\s<>"'=]+(?:=(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*\s*\/?>/gi,
                                '',
                            )
                            .replace(/&(lt|gt);/g, '');
                    } else {
                        valueStr = '';
                    }
                } else {
                    valueStr = value != null ? String(value) : '';
                }

                if ((!valueStr && !isRequired) || (valueStr && (!regExp || valueStr.match(regExp)))) {
                    return Promise.resolve();
                }

                return Promise.reject(
                    new Error(errorMessage ?? defaultErrorMessage),
                );
            },
        };
    });
    return [...rules, ...newRule];
};

export interface ILengthErrorProps {
    min?: number;
    max?: number;
}

const getSymbolWord = (length: number) => (
    length === 1 ? `${length} символа` : `${length} символов`
);

export const constructLengthErrorMessage = ({min, max}: ILengthErrorProps) => {
    const isMinValid = min && min > 0;
    const isMaxValid = max && max > 0;

    if (isMinValid && isMaxValid) {
        return `Данное поле должно содержать не менее ${getSymbolWord(min)} и не более ${getSymbolWord(max)}`;
    }
    if (isMinValid) {
        return `Данное поле должно содержать не менее ${getSymbolWord(min)}`;
    }
    if (isMaxValid) {
        return `Данное поле должно содержать не более ${getSymbolWord(max)}`;
    }

    return '';
};
